import React from 'react';

import {
  DSI,
  HANDSHAKE_INTERVAL_MS,
  HANDSHAKE_MAX_TRIES,
  asDsiMessage,
  postToFrame,
  previewOrigin,
  type DsiSourceInfo,
} from './live-preview';

export type SiteBridgeStatus = 'idle' | 'connecting' | 'ready' | 'failed';

export type SiteBridge = {
  /** `connecting` until the site answers the handshake; `failed` when it never does. */
  status: SiteBridgeStatus;
  /** The page URL the site last announced (follows in-site navigation). */
  url: string;
  /** The latest source list the site reported. */
  sources: DsiSourceInfo[];
  /** True once the first, quick source poll for the current page is over (its condition held, or it ran out).
   *  The bridge keeps asking, slowly, after that, so `sources` can still change. */
  sourcesSettled: boolean;
  /** True once the asking for the current page is over: the caller's condition held, or the slow poll after the quick
   *  one ran out too. Until then `sources` can still change; after it, nothing more is asked for this page. */
  sourcesDone: boolean;
  /** Wire to the iframe's `onLoad`: (re)arms the handshake for the freshly loaded page. */
  onFrameLoad: () => void;
  /** One source's content, or null when the site does not answer in time. */
  getSource: (dsId: string) => Promise<unknown>;
  /** Pin `content` over a data source (preview only, lost on reload). */
  setSource: (dsId: string, content: unknown) => void;
  /** Whether the site swallows clicks on data-source elements (the bridge boots with capture on). */
  setCapture: (enabled: boolean) => void;
};

const SOURCE_POLL_MS = 600;
const SOURCE_POLL_MAX = 25;
/** After the quick poll runs out, keep asking at this pace, this many times more (a minute), until the
 *  condition holds. A brand's site can take longer than the quick poll to report its lists (measured on
 *  dev-iulian: 11s and more), and a poll that stopped at 15s never heard them: the Games tab stayed on "No
 *  game lists on this page" for good. */
const SOURCE_SLOW_POLL_MS = 3000;
const SOURCE_SLOW_POLL_MAX = 20;
const GET_SOURCE_TIMEOUT_MS = 5000;
/** A site that cannot be framed never fires `load`, so give up on the connection after this long. */
const CONNECT_TIMEOUT_MS = 20000;

const asSources = (value: unknown): DsiSourceInfo[] =>
  Array.isArray(value)
    ? value.filter(
        (s): s is DsiSourceInfo => typeof s === 'object' && s !== null && typeof (s as { id?: unknown }).id === 'string',
      )
    : [];

/**
 * A generic ds-inspector connection to one framed brand site: the handshake, the trust-checked listener
 * (exact origin + this iframe's window), the page's data-source list and per-source reads and pins.
 *
 * After every `dsi-ready` (first connect or an in-site navigation) the source list is re-requested until
 * `sourcesUntil` holds, because the page's data sources populate the site store asynchronously: quickly at
 * first, then slowly for a while after `sourcesSettled` turns true.
 */
export const useSiteBridge = (
  frameSrc: string,
  iframeRef: React.RefObject<HTMLIFrameElement>,
  sourcesUntil: (sources: DsiSourceInfo[]) => boolean,
  /** The caller's key for the frame's load, when it reloads the same page by giving the frame a new key (Try again):
   *  each new one is waited for as a first load is, and given up on when the reloaded page never loads. */
  loadKey?: number,
): SiteBridge => {
  const origin = React.useMemo(() => previewOrigin(frameSrc), [frameSrc]);
  const [status, setStatus] = React.useState<SiteBridgeStatus>(frameSrc ? 'connecting' : 'idle');
  const [url, setUrl] = React.useState('');
  const [sources, setSources] = React.useState<DsiSourceInfo[]>([]);
  const [sourcesSettled, setSourcesSettled] = React.useState(false);
  const [sourcesDone, setSourcesDone] = React.useState(false);
  const [loadTick, setLoadTick] = React.useState(0);
  const [readyTick, setReadyTick] = React.useState(0);
  // The frame the state above belongs to. When the caller points the bridge at a new page, the previous page's
  // "ready" and sources are dropped WHILE RENDERING, not in the effect below: that effect runs after the caller's
  // own effects of the same commit have already seen the old page's "ready" and asked it for a source (which,
  // disconnected by then, answers null at once, and the caller took that as the source being unreadable).
  const [stateFor, setStateFor] = React.useState(frameSrc);
  if (stateFor !== frameSrc) {
    setStateFor(frameSrc);
    setUrl('');
    setSources([]);
    setSourcesSettled(false);
    setSourcesDone(false);
    setStatus(frameSrc ? 'connecting' : 'idle');
  }

  const connectedRef = React.useRef(false);
  // How many times the frame has loaded (`onFrameLoad`): a wait for a page that never loads ends once one has.
  const loadsRef = React.useRef(0);
  const urlRef = React.useRef('');
  const settledRef = React.useRef(false);
  // Whether the caller's condition held for the current page: the one thing that ends the asking early.
  const heldRef = React.useRef(false);
  const untilRef = React.useRef(sourcesUntil);
  untilRef.current = sourcesUntil;
  const waitersRef = React.useRef(new Map<string, Array<(content: unknown) => void>>());

  const post = React.useCallback(
    (type: string, payload?: Record<string, unknown>): void => {
      if (origin) postToFrame(iframeRef.current?.contentWindow, origin, type, payload);
    },
    [origin, iframeRef],
  );

  // Syncs with the iframe being pointed at a new page: forget the previous page's connection, and give up
  // if the new page never connects (e.g. the site refuses to be framed).
  React.useEffect(() => {
    connectedRef.current = false;
    urlRef.current = '';
    settledRef.current = false;
    heldRef.current = false;
    setUrl('');
    setSources([]);
    setSourcesSettled(false);
    setSourcesDone(false);
    setStatus(frameSrc ? 'connecting' : 'idle');
    const waiters = waitersRef.current;
    if (!frameSrc) return undefined;
    // Only for a page that never loads: one that loaded is given up on by its handshake, and a reload of it (a new
    // `loadKey`) by its own wait. Kept past a load, it failed a reload still within its handshake.
    const loadsBefore = loadsRef.current;
    const timer = window.setTimeout(() => {
      if (!connectedRef.current && loadsRef.current === loadsBefore) setStatus('failed');
    }, CONNECT_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timer);
      waiters.forEach((list) => list.forEach((resolve) => resolve(null)));
      waiters.clear();
    };
  }, [frameSrc]);

  // A reload of the same page (a new `loadKey`) waits for the reloaded page as the first load waits for its own: one that
  // never loads would otherwise leave the bridge saying what the last load said, for good. The first key is the first
  // load's, which the wait above covers.
  const firstLoadKey = React.useRef(loadKey);
  React.useEffect(() => {
    if (!frameSrc || loadKey === firstLoadKey.current) return undefined;
    const loadsBefore = loadsRef.current;
    const timer = window.setTimeout(() => {
      if (loadsRef.current === loadsBefore) setStatus('failed');
    }, CONNECT_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [frameSrc, loadKey]);

  const onFrameLoad = React.useCallback((): void => {
    loadsRef.current += 1;
    connectedRef.current = false;
    urlRef.current = '';
    setStatus('connecting');
    setLoadTick((n) => n + 1);
  }, []);

  // Subscribes to the framed site's messages.
  React.useEffect(() => {
    if (!origin) return undefined;
    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== origin || event.source !== iframeRef.current?.contentWindow) return;
      const msg = asDsiMessage(event.data);
      if (!msg) return;
      if (msg.type === DSI.READY) {
        const next = typeof msg.payload.url === 'string' ? msg.payload.url : '';
        // A set-capture re-ack carries the SAME url: only a first connect or a navigation restarts discovery.
        if (connectedRef.current && next === urlRef.current) return;
        connectedRef.current = true;
        urlRef.current = next;
        settledRef.current = false;
        heldRef.current = false;
        setUrl(next);
        setStatus('ready');
        setSourcesSettled(false);
        setSourcesDone(false);
        setReadyTick((n) => n + 1);
      } else if (msg.type === DSI.SOURCES) {
        const list = asSources(msg.payload.sources);
        setSources(list);
        if (!heldRef.current && untilRef.current(list)) {
          heldRef.current = true;
          setSourcesDone(true);
          if (!settledRef.current) {
            settledRef.current = true;
            setSourcesSettled(true);
          }
        }
      } else if (msg.type === DSI.SOURCE) {
        const dsId = typeof msg.payload.dsId === 'string' ? msg.payload.dsId : '';
        const waiting = waitersRef.current.get(dsId);
        if (!waiting) return;
        waitersRef.current.delete(dsId);
        waiting.forEach((resolve) => resolve(msg.payload.content ?? null));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origin, iframeRef]);

  // Handshake: after each load, re-send dsi-init until the bridge answers (it is inert until then).
  React.useEffect(() => {
    if (!loadTick || !origin) return undefined;
    let tries = 0;
    post(DSI.INIT);
    const timer = window.setInterval(() => {
      if (connectedRef.current) {
        window.clearInterval(timer);
        return;
      }
      if (++tries >= HANDSHAKE_MAX_TRIES) {
        window.clearInterval(timer);
        setStatus('failed');
        return;
      }
      post(DSI.INIT);
    }, HANDSHAKE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadTick, origin, post]);

  // Source discovery for the connected page: ask until the caller's condition holds. Quickly at first; when
  // that runs out the page counts as settled (the caller may say it has nothing), and the asking goes on
  // slowly for a while, so a site that reports late is still heard.
  React.useEffect(() => {
    if (!readyTick) return undefined;
    let tries = 0;
    let timer = 0;
    const ask = (): void => {
      if (heldRef.current) return;
      tries += 1;
      if (tries === SOURCE_POLL_MAX && !settledRef.current) {
        settledRef.current = true;
        setSourcesSettled(true);
      }
      if (tries >= SOURCE_POLL_MAX + SOURCE_SLOW_POLL_MAX) {
        // A slow-poll interval after the last ask, so its answer is in.
        setSourcesDone(true);
        return;
      }
      post(DSI.LIST_SOURCES);
      timer = window.setTimeout(ask, tries < SOURCE_POLL_MAX ? SOURCE_POLL_MS : SOURCE_SLOW_POLL_MS);
    };
    post(DSI.LIST_SOURCES);
    timer = window.setTimeout(ask, SOURCE_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [readyTick, post]);

  const getSource = React.useCallback(
    (dsId: string): Promise<unknown> =>
      new Promise((resolve) => {
        if (!origin || !connectedRef.current) {
          resolve(null);
          return;
        }
        let timer = 0;
        let settled = false;
        const done = (content: unknown): void => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          resolve(content);
        };
        timer = window.setTimeout(() => {
          const rest = (waitersRef.current.get(dsId) ?? []).filter((w) => w !== done);
          if (rest.length) waitersRef.current.set(dsId, rest);
          else waitersRef.current.delete(dsId);
          done(null);
        }, GET_SOURCE_TIMEOUT_MS);
        waitersRef.current.set(dsId, [...(waitersRef.current.get(dsId) ?? []), done]);
        post(DSI.GET_SOURCE, { dsId });
      }),
    [origin, post],
  );

  const setSource = React.useCallback(
    (dsId: string, content: unknown): void => post(DSI.SET_SOURCE, { dsId, content }),
    [post],
  );

  const setCapture = React.useCallback((enabled: boolean): void => post(DSI.SET_CAPTURE, { enabled }), [post]);

  return { status, url, sources, sourcesSettled, sourcesDone, onFrameLoad, getSource, setSource, setCapture };
};
